import React from "react";
import { Alert, Box, Button, Grid, Paper, Typography } from "@mui/material";
import { VisibilityOutlined } from "@mui/icons-material";
import moment from "moment";
import { Link as RouterLink } from "react-router-dom";
import ItemProgressTracker from "../../order/component/ItemProgressTracker";
import CustomerContactInfoButton from "../../common/component/CustomerContactInfoButton";
import HTMLViewer from "../../common/component/HTMLViewer";
import LineItemDetails from "../../common/component/lineitem/LineItemDetails";
import ImageCarousel from "../../common/component/ImageCarousel";
import { IMG_PLACEHOLDER, STORAGE_BUCKET } from "../../common/constants";
import { getItemProgress } from "../../common/util";

export default function KitchenOrder(props) {
  const { order, focusIndex, onItemProgressChange, basePath = "/production/tracker/order" } = props;
  const itemPath = (idx) => `${basePath}/${order.id}/item/${idx}`;

  const focusItem = Number.isInteger(focusIndex) ? order.items[focusIndex] : undefined;
  const otherItems = order.items
    .map((item, idx) => ({ item, idx }))
    .filter(({ idx }) => idx !== focusIndex);

  const renderItem = (item, idx) => {
    return (
      <Box key={idx} my={2}>
        <Grid container spacing={2}>
          <Grid item xs={1} textAlign={"center"}>
            <Box
              sx={{
                width: "40px",
                height: "40px",
                border: "1px solid #DDD",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">{item.quantity} </Typography>
            </Box>
            <Typography variant="overline">QTY</Typography>
          </Grid>
          <Grid item xs={5}>
            <ImageCarousel images={item.images} />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6" gutterBottom>
              {item.title}
            </Typography>
            <LineItemDetails item={item} />
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderItemCard = (item, idx) => (
    <Paper key={idx} elevation={1} sx={{ mb: 2 }}>
      <Box px={2} py={1}>
        {renderItem(item, idx)}
      </Box>
    </Paper>
  );

  const renderProgressTracker = (item, idx) => {
    const progress = getItemProgress(order, item);
    return (
      <Box mt={3} mb={2}>
        <Typography variant="overline" color="textSecondary">
          PROGRESS TRACKER
        </Typography>
        <Paper elevation={1}>
          <Box px={2} py={2}>
            <Typography variant="body2" color="textSecondary">
              Green ticks are completed steps. Click a step to mark it complete, or click a
              completed step to reopen it, then save your changes.
            </Typography>
            <ItemProgressTracker
              key={progress.step}
              orderId={order.id}
              item={item}
              itemIndex={idx}
              progress={progress}
              onChange={onItemProgressChange}
            />
          </Box>
        </Paper>
      </Box>
    );
  };

  const renderItemSummary = (item, idx) => {
    const image = item.images?.find(Boolean) || `${STORAGE_BUCKET}${IMG_PLACEHOLDER}`;
    const details = item.variant?.size?.name || "";

    return (
      <Paper key={idx} elevation={1} sx={{ mb: 1 }}>
        <Box px={2} py={1.5} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <img
            alt=""
            src={image}
            style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" }}
          />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap>
              {`${item.quantity || 1}x ${item.title || item.type}`}
            </Typography>
            {details && (
              <Typography variant="body2" color="textSecondary" noWrap>
                {details}
              </Typography>
            )}
          </Box>
          <Button
            component={RouterLink}
            to={itemPath(idx)}
            color="secondary"
            startIcon={<VisibilityOutlined />}
            sx={{ flexShrink: 0 }}
          >
            VIEW
          </Button>
        </Box>
      </Paper>
    );
  };

  let alert = "";
  if (order.status === "fulfilled" || order.status === "completed") {
    alert = <Alert severity="success">This order has been completed</Alert>;
  } else if (order.status === "canceled") {
    alert = <Alert severity="error">This order has been canceled</Alert>;
  }

  const addOns = order.invoice
    ? Object.entries(order.invoice).filter(([k, v]) => v.status !== "void")
    : [];

  const renderInstructions = () =>
    order.kitchen ? (
      <Box mt={3} mb={2}>
        <Typography variant="overline" color="textSecondary">
          INSTRUCTIONS
        </Typography>
        <Paper elevation={1}>
          <Box px={2} py={2}>
            <HTMLViewer content={order.kitchen.notes} style={{ fontSize: ".85rem" }} />
          </Box>
        </Paper>
      </Box>
    ) : null;

  const renderAddOns = () =>
    addOns.length > 0 ? (
      <Box mt={3} mb={2}>
        <Typography variant="overline" color="textSecondary">
          ADD-ON
        </Typography>
        <Paper elevation={1}>
          <Box px={2} py={2}>
            {addOns.map(([key, value]) => (
              <Box key={key} mb={addOns.length > 1 ? 1 : 0}>
                <Typography variant="body2">{value.description}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
    ) : null;

  return (
    <Box my={6}>
      <Grid container alignItems={"center"}>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", gap: "5px", alignItems: "center", flexWrap: "wrap" }}>
            <CustomerContactInfoButton customer={order.customer} />
            <Typography
              variant="overline"
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: ".8rem",
                color: "#777",
                gap: "4px",
              }}
            >
              <span style={{ color: "#CCC" }}>{` | `}</span>
              {moment(order.fulfillment.date).format("ddd, MMM D")} [{order.fulfillment.time}]
            </Typography>
            <Typography
              variant="overline"
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: ".8rem",
                color: "#777",
                gap: "4px",
              }}
            >
              <span style={{ color: "#CCC" }}>{` ● `}</span>
              {order.fulfillment.type}
            </Typography>
            {order.fulfillment.type === "pickup" && (
              <Typography
                variant="overline"
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: ".8rem",
                  color: "#777",
                  gap: "4px",
                }}
              >
                <span style={{ color: "#CCC" }}>{` ● `}</span>
                {order.fulfillment.location
                  ? order.fulfillment.location.street
                  : order.fulfillment.address.split(",")[0]}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
      {alert && <Box mb={2}>{alert}</Box>}
      {focusItem ? (
        <>
          {renderItemCard(focusItem, focusIndex)}
          {renderInstructions()}
          {renderAddOns()}
          {renderProgressTracker(focusItem, focusIndex)}
        </>
      ) : (
        <Box mb={2}>
          {order.items.map((item, idx) => renderItemSummary(item, idx))}
          {renderInstructions()}
          {renderAddOns()}
        </Box>
      )}
      {focusItem && otherItems.length > 0 && (
        <Box mt={3}>
          <Typography variant="overline" color="textSecondary">
            Other items in this order
          </Typography>
          {otherItems.map(({ item, idx }) => renderItemSummary(item, idx))}
        </Box>
      )}
    </Box>
  );
}
